import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslationService, LANG } from '../../services/translation/translation.service';
import { MenuItem } from './menuitem';
import { AuthenticationService } from '../../services/security/authentication.service';
import { ProfileService } from '../../services/security/profile.service';

/**
 * List of user roles
 */
const ROLES = {
    twroute: 'SHIPPERTMS_ROUTE_',
    twcalloff: 'SHIPPERTMS_CO_',
    twslot: 'SHIPPERTMS_SLOT_',
    slotbooking: 'SLOT_',
    twdoc: 'SHIPPERTMS_DOC_',
    twcost: 'SHIPPERTMS_COST_'
};

/**
 * Menu component is always shown on the top of the page
 */
@Component({
    selector: 'tw-menu',
    templateUrl: 'menu.html',
    styleUrls: ['menu.scss']
})
export class MenuComponent implements OnInit {
    /**
     * hoverMenu - true if mouseenter on menu (used for small screens: mobile, tablets)
     * @type {boolean}
     */
    public hoverMenu: boolean = false;

    /**
     * Left side items
     * @type {MenuItem[]}
     */
    public menuLeftItems: MenuItem[] = null;

    /**
     * Right side items
     * @type {any[]}
     */
    public menuRightItems: MenuItem[] = [];

    /**
     * Lang menu
     * @type {any[]}
     */
    public langMenu: MenuItem;

    /**
     * twaBase url from env.json config
     * @type {string}
     */
    public readonly twaBase: string = '';

    /**
     * The "constructor"
     * @param {TranslationService} translate - translation for menu item labels
     * @param {Router} router - go to the link inside of Angular app
     * @param {AuthenticationService} authenticationService - needed only for logout func
     * @param {ProfileService} profile - user profile
     */
    constructor(private translate: TranslationService,
                private router: Router,
                private authenticationService: AuthenticationService,
                private profile: ProfileService) {
    }

    /**
     * Menu component init on ngOnInit lifecycle hook
     */
    public ngOnInit() {
        this.initRightItems();
        this.initLangManu();
    }

    /**
     * Logout (not used for the moment, because of aws cognito logout issues)
     */
    public logoff(): void {
        this.authenticationService.logout();
        this.router.navigate(['login']);
    }

    /**
     * Redirects to the link from menu item. If 'external' true, opens page in new tab, else goes to route inside app.
     * @param {MenuItem} item
     * @returns {any}
     */
    public goTo(item: MenuItem): any {
        return item.external ? window.open(item.link, '_blank') : this.router.navigate([item.link]);
    }

    /**
     * Get user name
     * @returns {string} user name (if user logged in)
     */
    public getUserName() {
        return this.profile.getUserName();
    }

    public isMockProfile() {
        return this.profile.isLogged() && this.profile.isMockProfile();
    }

    public chooseLang(lang: string) {
        this.translate.use(lang);
        this.langMenu.name = this.translate.currentLang;
    }

    /**
     * Get list of left menu items. Only if user logged in and user roles were set.
     * @returns {MenuItem[]} left menu items
     */
    public getMenuLeftItems() {
        if (this.menuLeftItems === null) {
            this.menuLeftItems = [];
            this.menuLeftItems.push(new MenuItem({
                name: 'lvt.menu.twdoc',
                link: 'audit',
                active: true
            }));
        }
        return this.menuLeftItems;
    }

    /**
     * Get list of right menu items. List is ready right after menu component initialization.
     */
    private initRightItems() {
        this.menuRightItems = [
            new MenuItem({styleClass: 'custom-icon-question-mark help-ico', type: 'inline-manual'}),
            new MenuItem({styleClass: 'custom-icon-menu-home', external: true, link: this.twaBase}),
            new MenuItem({styleClass: 'custom-icon-menu-chat', external: true, link: this.twaBase}),
            new MenuItem({
                styleClass: 'custom-icon-menu-profile',
                subItems: [
                    new MenuItem({type: 'username'}),
                    new MenuItem({divider: true}),
                    new MenuItem({
                        name: 'lvt.menu.my_profile', external: true,
                        link: `${this.twaBase}AdminModule.html#community/myaccount/myaccount`
                    }),
                    new MenuItem({
                        name: 'lvt.menu.tw_profile', external: true,
                        link: `${this.twaBase}uma/retrieveUserProfile.do?`
                    }),
                    new MenuItem({
                        name: 'lvt.menu.company_profile', external: true,
                        link: `${this.twaBase}uma/retrieveCompanyProfile.do?`
                    }),
                    new MenuItem({
                        name: 'lvt.menu.my_notifications', external: true,
                        link: `${this.twaBase}uma/retrieveUserProfile.do?type=notifications&app=cms`
                    }),
                    new MenuItem({divider: true}),
                    new MenuItem({name: 'lvt.menu.sign_out', type: 'logout'})
                ]
            })];
    }

    /**
     * Init lang menu
     */
    private initLangManu() {
        let langList = Object.keys(LANG).map(key => new MenuItem({name: LANG[key]}));
        this.langMenu = new MenuItem({
            name: this.translate.currentLang,
            subItems: langList
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/components/menu/menu.component.ts